import { useState } from 'react';
import { useQuery } from 'react-query';

import { ErrorResponse } from 'legacy/common/utils/error';

/**
 * A custom hook that provides pagination functionality using cursors to the REGULAR react query hook.
 */
export const useCursorQuery = <
  T = { nextCursor?: string; previousCursor?: string; items: unknown[] },
  U = ErrorResponse,
  V = T
>(
  ...queryParams
) => {
  const [queryKey, queryFn, queryOptions] = queryParams;
  const [previousCursor, setPreviousCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [fetchCursor, setFetchCursor] = useState(null);

  const query = useQuery<T, U, V>(
    [...queryKey, fetchCursor],
    (queryData) => queryFn(queryData, fetchCursor),
    {
      ...queryOptions,
      onSuccess: (data: {
        nextCursor?: string;
        previousCursor?: string;
        items: unknown[];
      }) => {
        setNextCursor(data.nextCursor);
        setPreviousCursor(data.previousCursor);
        queryOptions?.onSuccess?.(data);
      }
    }
  );

  const fetchNextPage = () => {
    setFetchCursor(nextCursor);
  };

  const fetchPreviousPage = () => {
    setFetchCursor(previousCursor);
  };

  return {
    ...query,
    hasNextPage: !!nextCursor,
    hasPreviousPage: !!previousCursor,
    fetchNextPage,
    fetchPreviousPage
  };
};
